import { BoxGeometry, ConeGeometry, CylinderGeometry, DodecahedronGeometry, IcosahedronGeometry, SphereGeometry, type BufferGeometry } from "three";
import { MODELS, RESOURCE_MODELS } from "../assets/models.generated.ts";
import { lumpy, merge, type Piece } from "./mesh-kit.ts";
import type { ModelLibrary } from "./model-library.ts";

/** Malhas procedurais dos nós de coleta, montadas com o kit de malhas; os tipos com modelo importado (ADR 0009) só as usam como reserva. */
const C = {
  trunk: "#6E4B33",
  trunkLight: "#9C8F7A",
  leaf: "#2F7A3A",
  leafLight: "#4F9A3E",
  leafPale: "#7FAF6A",
  palm: "#5E9A3A",
  rock: "#8A8578",
  rockDark: "#5F5C55",
  coal: "#2A2826",
  rust: "#A0522D",
  silver: "#D8DCE0",
  mud: "#5E5340",
  straw: "#C9B25A",
  banana: "#E8C547",
  red: "#C8402E",
  coconut: "#6B4A2E",
  fish: "#2E4B5A",
  /** Copa neutra: a cor (amarelo ou rosa) vem da instância. */
  ipe: "#FFF4E2",
};

type Builder = () => BufferGeometry;

function treeNear(trunkH: number, trunkR: number, crown: Piece[], trunkColor = C.trunk): BufferGeometry {
  return merge([
    { geometry: new CylinderGeometry(trunkR * 0.7, trunkR, trunkH, 6), color: trunkColor, wind: [0, 0.15], position: [0, trunkH / 2, 0] },
    ...crown,
  ]);
}

function palm(trunkH: number, fronds: number, frondLen: number, color: string, extra: Piece[] = []): BufferGeometry {
  const pieces: Piece[] = [];
  const segments = 4;
  for (let i = 0; i < segments; i++) {
    const y = (trunkH / segments) * (i + 0.5);
    pieces.push({
      geometry: new CylinderGeometry(0.16, 0.2, trunkH / segments + 0.05, 6),
      color: C.trunkLight,
      wind: [0.05 * i, 0.05 * (i + 1)],
      position: [i * i * 0.06, y, 0],
      rotation: [0, 0, -0.06 * i],
    });
  }
  const top = (segments - 1) ** 2 * 0.06;
  for (let i = 0; i < fronds; i++) {
    const a = (i / fronds) * Math.PI * 2;
    pieces.push({
      geometry: new BoxGeometry(frondLen, 0.06, 0.45),
      color,
      wind: [0.6, 1],
      position: [top + Math.cos(a) * frondLen * 0.45, trunkH - 0.1, Math.sin(a) * frondLen * 0.45],
      rotation: [0, -a, -0.45],
    });
  }
  return merge([...pieces, ...extra.map((p) => ({ ...p, position: [top + (p.position?.[0] ?? 0), p.position?.[1] ?? trunkH, p.position?.[2] ?? 0] as const }))]);
}

function rock(colors: string[], scale = 1, spots = 0): BufferGeometry {
  const pieces: Piece[] = [{ geometry: lumpy(new DodecahedronGeometry(0.9 * scale, 0), 0.35, scale * 7), color: colors[0] ?? C.rock, position: [0, 0.45 * scale, 0], scale: [1.2, 0.8, 1] }];
  for (let i = 0; i < spots; i++) {
    const a = (i / spots) * Math.PI * 2 + 0.4;
    pieces.push({ geometry: new IcosahedronGeometry(0.22 * scale, 0), color: colors[1] ?? C.rockDark, position: [Math.cos(a) * 0.75 * scale, 0.5 * scale + (i % 2) * 0.25, Math.sin(a) * 0.6 * scale] });
  }
  return merge(pieces);
}

/** Copa arredondada de várias esferas achatadas. */
function crown(y: number, radius: number, color: string, lobes: number, flat = 0.7, alt = C.leafLight): Piece[] {
  const pieces: Piece[] = [{ geometry: new IcosahedronGeometry(radius, 0), color, wind: [0.6, 1], position: [0, y, 0], scale: [1, flat, 1] }];
  for (let i = 0; i < lobes; i++) {
    const a = (i / lobes) * Math.PI * 2;
    pieces.push({ geometry: new IcosahedronGeometry(radius * 0.65, 0), color: i % 2 ? alt : color, wind: [0.6, 1], position: [Math.cos(a) * radius * 0.7, y - radius * 0.15, Math.sin(a) * radius * 0.7], scale: [1, flat, 1] });
  }
  return pieces;
}

const NEAR: Record<string, Builder> = {
  galhada: () =>
    merge([0, 1, 2, 3].map((i) => ({ geometry: new BoxGeometry(1.4, 0.09, 0.09), color: i % 2 ? C.trunk : C.trunkLight, position: [0, 0.06 + i * 0.05, 0], rotation: [0, i * 0.8, 0.05 * i] }))),
  moita: () => merge([0, 1, 2, 3, 4].map((i) => ({ geometry: new ConeGeometry(0.12, 0.9, 4), color: i % 2 ? C.straw : C.leafPale, wind: [0, 1], position: [Math.cos(i * 1.3) * 0.2, 0.45, Math.sin(i * 1.3) * 0.2], rotation: [Math.cos(i) * 0.3, 0, Math.sin(i) * 0.3] }))),
  bananeira: () =>
    merge([
      { geometry: new CylinderGeometry(0.16, 0.22, 2.2, 6), color: C.leafPale, wind: [0, 0.2], position: [0, 1.1, 0] },
      ...[0, 1, 2, 3, 4].map((i) => ({ geometry: new BoxGeometry(1.6, 0.04, 0.5), color: C.leafLight, wind: [0.5, 1] as const, position: [Math.cos(i * 1.25) * 0.75, 2.3, Math.sin(i * 1.25) * 0.75] as const, rotation: [0, -i * 1.25, -0.5] as const })),
      { geometry: new SphereGeometry(0.22, 5, 4), color: C.banana, wind: [0.3, 0.4], position: [0.15, 1.8, 0], scale: [1, 1.6, 1] },
    ]),
  embauba: () =>
    treeNear(7, 0.22, [
      ...[0, 1, 2, 3, 4, 5].map((i) => ({ geometry: new CylinderGeometry(1.0, 1.1, 0.12, 7), color: i % 2 ? C.leafPale : "#9DBF8A", wind: [0.7, 1] as const, position: [Math.cos(i) * 0.9, 6.6 + (i % 3) * 0.35, Math.sin(i) * 0.9] as const, rotation: [0.15 * Math.cos(i * 2), 0, 0.15 * Math.sin(i * 2)] as const })),
    ], C.trunkLight),
  cajueiro: () =>
    merge([
      { geometry: new CylinderGeometry(0.2, 0.32, 1.6, 6), color: C.trunk, wind: [0, 0.1], position: [0, 0.8, 0], rotation: [0, 0, 0.25] },
      { geometry: new CylinderGeometry(0.14, 0.2, 1.4, 5), color: C.trunk, wind: [0.1, 0.3], position: [0.45, 1.9, 0], rotation: [0, 0, -0.5] },
      ...crown(3.0, 1.6, C.leaf, 5, 0.55),
      ...[0, 1, 2].map((i) => ({ geometry: new IcosahedronGeometry(0.12, 0), color: i % 2 ? C.red : C.banana, wind: [0.7, 0.8] as const, position: [Math.cos(i * 2.1) * 1.3, 2.5, Math.sin(i * 2.1) * 1.3] as const })),
    ]),
  coqueiro: () => palm(7.5, 7, 2.6, C.palm, [0, 1, 2].map((i) => ({ geometry: new IcosahedronGeometry(0.18, 0), color: C.coconut, wind: [0.4, 0.5] as const, position: [Math.cos(i * 2.1) * 0.25, 7.2, Math.sin(i * 2.1) * 0.25] as const }))),
  acaizeiro: () => palm(8, 6, 1.8, C.leaf, [0, 1].map((i) => ({ geometry: new SphereGeometry(0.2, 5, 4), color: "#3B1F4A", wind: [0.4, 0.5] as const, position: [0.2 * (i ? 1 : -1), 7.3, 0] as const, scale: [1, 1.8, 1] as const }))),
  ipe: () => treeNear(5.5, 0.3, crown(6.2, 2.2, C.ipe, 5, 0.6, "#F2E6D0")),
  jatoba: () => treeNear(7.5, 0.5, crown(8.6, 3.2, "#24612E", 6, 0.65, C.leaf)),
  angelim: () => treeNear(12, 0.75, [...crown(13.5, 4.2, "#2B5E2A", 7, 0.55, "#3D7A33"), ...crown(11.5, 2.4, C.leaf, 3, 0.6)]),
  rocha: () => rock([C.rock, C.rockDark], 1, 0),
  barreiro: () => merge([{ geometry: lumpy(new SphereGeometry(1.2, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2), 0.25, 3), color: C.mud, position: [0, -0.15, 0], scale: [1.3, 0.45, 1.1] }]),
  "veio-carvao": () => rock([C.rockDark, C.coal], 0.9, 5),
  "veio-ferro": () => rock([C.rock, C.rust], 0.95, 6),
  "veio-prata": () => rock([C.rockDark, C.silver], 0.95, 6),
  // Peixes na origem: a camada posiciona o cardume logo abaixo da superfície, não no fundo.
  cardume: () => merge([0, 1, 2, 3, 4].map((i) => ({ geometry: new SphereGeometry(0.18, 5, 3), color: C.fish, position: [Math.cos(i * 1.7) * 0.8, (i % 2) * 0.08, Math.sin(i * 1.7) * 0.8] as const, scale: [1.8, 0.3, 0.7] as const, rotation: [0, i * 1.7, 0] as const }))),
};

/** Silhueta barata para longe: árvores viram cone sobre tronco; o resto reaproveita a malha próxima. */
const FAR: Partial<Record<string, Builder>> = {
  embauba: () => treeNear(7, 0.22, [{ geometry: new CylinderGeometry(1.8, 1.8, 0.6, 6), color: C.leafPale, wind: [0.7, 1], position: [0, 6.8, 0] }], C.trunkLight),
  cajueiro: () => treeNear(1.8, 0.3, [{ geometry: new IcosahedronGeometry(2, 0), color: C.leaf, wind: [0.6, 1], position: [0, 3, 0], scale: [1, 0.55, 1] }]),
  coqueiro: () => treeNear(7.5, 0.18, [{ geometry: new ConeGeometry(2.4, 1.2, 6), color: C.palm, wind: [0.6, 1], position: [0.5, 7.2, 0] }], C.trunkLight),
  acaizeiro: () => treeNear(8, 0.15, [{ geometry: new ConeGeometry(1.7, 1.0, 5), color: C.leaf, wind: [0.6, 1], position: [0.5, 7.6, 0] }], C.trunkLight),
  ipe: () => treeNear(5.5, 0.3, [{ geometry: new IcosahedronGeometry(2.6, 0), color: C.ipe, wind: [0.6, 1], position: [0, 6.2, 0], scale: [1, 0.6, 1] }]),
  jatoba: () => treeNear(7.5, 0.5, [{ geometry: new IcosahedronGeometry(3.6, 0), color: "#24612E", wind: [0.6, 1], position: [0, 8.6, 0], scale: [1, 0.65, 1] }]),
  angelim: () => treeNear(12, 0.75, [{ geometry: new IcosahedronGeometry(4.8, 0), color: "#2B5E2A", wind: [0.6, 1], position: [0, 13.5, 0], scale: [1, 0.55, 1] }]),
  bananeira: () => treeNear(2.2, 0.2, [{ geometry: new ConeGeometry(1.2, 0.8, 5), color: C.leafLight, wind: [0.6, 1], position: [0, 2.3, 0] }], C.leafPale),
};

const cache = new Map<string, BufferGeometry>();

export function resourceGeometry(typeId: string, lod: "near" | "far"): BufferGeometry {
  const key = `${typeId}:${lod}`;
  let g = cache.get(key);
  if (!g) {
    const builder = (lod === "far" ? FAR[typeId] : undefined) ?? NEAR[typeId];
    if (!builder) throw new Error(`sem malha para o recurso ${typeId}`);
    g = builder();
    cache.set(key, g);
  }
  return g;
}

export const RESOURCE_MESH_IDS = Object.keys(NEAR);

export interface ResourceMeshes {
  /** Malha de perto de cada variante (cada instância sorteia uma pelo id). */
  near: BufferGeometry[];
  /** Silhueta de longe, uma por tipo (o LOD 1 da primeira variante): um draw call só. */
  far: BufferGeometry;
  /** Paletas das malhas, na ordem dos atributos de cor (a primeira é a padrão); vazio nas procedurais. */
  palettes: readonly string[];
}

/**
 * Malhas de um tipo de recurso: os modelos importados (F37/F42) quando o tipo tem e a biblioteca
 * carregou; senão, a malha procedural como variante única.
 */
export function resourceMeshes(typeId: string, library: ModelLibrary): ResourceMeshes {
  const ids = (RESOURCE_MODELS[typeId] ?? []).filter((id) => library.get(id, 0));
  const first = ids[0];
  if (first === undefined) return { near: [resourceGeometry(typeId, "near")], far: resourceGeometry(typeId, "far"), palettes: [] };
  return { near: ids.map((id) => library.get(id, 0)!), far: library.get(first, 1) ?? library.get(first, 0)!, palettes: MODELS[first].palettes };
}
